import React, { useEffect, useState } from "react";
import { Key, Trash2, Copy, Check, AlertTriangle, Plus, Edit2, X, BookOpen } from "lucide-react";
import "./ApiKeyManager.css";
import { createApiKey, listApiKeys, revokeApiKey, updateApiKey } from "../services/apiClient";
import ApiDocumentation from "./ApiDocumentation";

function ApiKeyManager() {
  const [activeTab, setActiveTab] = useState("keys");
  const [apiKeys, setApiKeys] = useState([]);
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState(["read"]);
  const [ipWhitelist, setIpWhitelist] = useState("");
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  
  // Edit modal state
  const [editingKey, setEditingKey] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPermissions, setEditPermissions] = useState([]);
  const [editIpWhitelist, setEditIpWhitelist] = useState("");

  const loadKeys = async () => {
    try {
      const response = await listApiKeys();
      setApiKeys(response.apiKeys || []);
    } catch (err) {
      console.error("Failed to load API keys", err);
      setError("Failed to load API keys");
    }
  };

  useEffect(() => {
    if (activeTab === "keys") {
      loadKeys();
    }
  }, [activeTab]);

  const togglePermission = (permission) => {
    setPermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission);
      }
      return [...prev, permission];
    });
  };

  const toggleEditPermission = (permission) => {
    setEditPermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission);
      }
      return [...prev, permission];
    });
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError("Key name is required");
      return;
    }
    if (permissions.length === 0) {
      setError("At least one permission is required");
      return;
    }
    
    setError(null);
    setLoading(true);
    
    try {
      const ipList = ipWhitelist
        .split(",")
        .map((ip) => ip.trim())
        .filter(Boolean);
      
      const payload = {
        name: name.trim(),
        permissions,
      };
      
      if (ipList.length > 0) {
        payload.ipWhitelist = ipList;
      }
      
      console.log("Creating API key with payload:", payload);
      const response = await createApiKey(payload);
      console.log("API key created:", response);
      
      setCreatedCredentials({
        apiKey: response.apiKey,
        apiSecret: response.apiSecret,
      });
      setName("");
      setIpWhitelist("");
      setPermissions(["read"]);
      await loadKeys();
    } catch (err) {
      console.error("Failed to create API key", err);
      const errorMsg = err.response?.data?.error || err.response?.data?.details || err.message || "Failed to create API key";
      setError(typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Are you sure you want to revoke this API key?")) return;
    try {
      await revokeApiKey(id);
      await loadKeys();
    } catch (err) {
      setError("Failed to revoke API key");
    }
  };

  const openEditModal = (key) => {
    setEditingKey(key);
    setEditName(key.name);
    setEditPermissions([...(key.permissions || [])]);
    setEditIpWhitelist((key.ip_whitelist || []).join(", "));
  };

  const closeEditModal = () => {
    setEditingKey(null);
    setEditName("");
    setEditPermissions([]);
    setEditIpWhitelist("");
  };

  const handleUpdate = async () => {
    if (!editingKey) return;
    if (!editName.trim()) {
      setError("Key name is required");
      return;
    }
    if (editPermissions.length === 0) {
      setError("At least one permission is required");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const ipList = editIpWhitelist
        .split(",")
        .map((ip) => ip.trim())
        .filter(Boolean);

      const payload = {
        name: editName.trim(),
        permissions: editPermissions,
        ipWhitelist: ipList.length > 0 ? ipList : null,
      };

      await updateApiKey(editingKey.id, payload);
      closeEditModal();
      await loadKeys();
    } catch (err) {
      console.error("Failed to update API key", err);
      const errorMsg = err.response?.data?.error || err.message || "Failed to update API key";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  // Mask API key: show first 8 chars + ...
  const maskApiKey = (key) => {
    if (!key || key.length <= 12) return key;
    return key.substring(0, 12) + "...";
  };

  return (
    <div className="api-keys-manager">
      <div className="section-header-tabs">
        <button 
          className={`tab-button ${activeTab === "keys" ? "active" : ""}`}
          onClick={() => setActiveTab("keys")}
        >
          <Key size={16} />
          API Keys
        </button>
        <button 
          className={`tab-button ${activeTab === "docs" ? "active" : ""}`}
          onClick={() => setActiveTab("docs")}
        >
          <BookOpen size={16} />
          API Documentation
        </button>
      </div>

      {activeTab === "keys" && (
        <div className="api-keys-content">
          {error && (
            <div className="error-banner">
              <AlertTriangle size={16} />
              <span>{error}</span>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {/* Create Form */}
          <div className="api-key-create-form">
            <div className="form-row">
              <input
                type="text"
                placeholder="Key name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-name"
              />
              <button 
                onClick={handleCreate} 
                disabled={loading || !name.trim() || permissions.length === 0}
                className="btn-create"
              >
                <Plus size={14} />
                <span>{loading ? "Creating..." : "Create"}</span>
              </button>
            </div>
            
            <div className="form-options">
              <div className="permissions-group">
                <span className="label">Permissions:</span>
                {["read", "transfer", "admin"].map((perm) => (
                  <label key={perm} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={permissions.includes(perm)}
                      onChange={() => togglePermission(perm)}
                    />
                    <span className="checkmark"></span>
                    <span className="checkbox-text">{perm}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="form-row-secondary">
              <input
                type="text"
                placeholder="IP whitelist (optional, comma separated)"
                value={ipWhitelist}
                onChange={(e) => setIpWhitelist(e.target.value)}
                className="input-ip"
              />
            </div>
          </div>

          {/* Created Credentials */}
          {createdCredentials && (
            <div className="credentials-box">
              <div className="credentials-warning">
                <AlertTriangle size={16} />
                <span>Store these credentials now. They will not be shown again.</span>
              </div>
              <div className="credential-row">
                <span className="credential-label">API Key</span>
                <code className="credential-value">{createdCredentials.apiKey}</code>
                <button 
                  className="btn-copy"
                  onClick={() => copyToClipboard(createdCredentials.apiKey, 'key')}
                >
                  {copied === 'key' ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
              <div className="credential-row">
                <span className="credential-label">Secret Key</span>
                <code className="credential-value">{createdCredentials.apiSecret}</code>
                <button 
                  className="btn-copy"
                  onClick={() => copyToClipboard(createdCredentials.apiSecret, 'secret')}
                >
                  {copied === 'secret' ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
              <button 
                className="btn-dismiss"
                onClick={() => setCreatedCredentials(null)}
              >
                I've saved these credentials
              </button>
            </div>
          )}

          {/* Keys List - Split by Active/Inactive */}
          {apiKeys.length === 0 ? (
            <div className="api-keys-list">
              <div className="empty-state">No API keys created yet</div>
            </div>
          ) : (
            <>
              {/* Active Keys Section */}
              {apiKeys.filter(k => !k.revoked_at).length > 0 && (
                <div className="api-keys-section">
                  <h4 className="section-title">
                    <span className="status-dot active"></span>
                    Active Keys
                    <span className="section-count">{apiKeys.filter(k => !k.revoked_at).length}</span>
                  </h4>
                  <div className="api-keys-list">
                    {apiKeys.filter(k => !k.revoked_at).map((key) => (
                      <div key={key.id} className="api-key-item">
                        <div className="key-info">
                          <div className="key-header">
                            <span className="key-name">{key.name}</span>
                            <code className="key-preview">{maskApiKey(key.id)}</code>
                            <button 
                              className="btn-copy-small"
                              onClick={() => copyToClipboard(key.id, key.id)}
                              title="Copy key ID"
                            >
                              {copied === key.id ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                            {key.created_at && (
                              <span className="key-date">
                                {new Date(key.created_at).toLocaleDateString(undefined, { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            )}
                          </div>
                          <div className="key-tags">
                            {(key.permissions || []).map((perm) => (
                              <span key={perm} className="tag tag-permission">{perm}</span>
                            ))}
                            {key.ip_whitelist && key.ip_whitelist.length > 0 ? (
                              <span className="tag tag-ip" title={key.ip_whitelist.join(", ")}>
                                IPs: {key.ip_whitelist.length}
                              </span>
                            ) : (
                              <span className="tag tag-no-ip">No IP whitelist</span>
                            )}
                          </div>
                        </div>
                        <div className="key-actions">
                          <button 
                            className="btn-edit"
                            onClick={() => openEditModal(key)}
                            title="Edit key"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button 
                            className="btn-revoke"
                            onClick={() => handleRevoke(key.id)}
                            title="Revoke key"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Revoked Keys Section */}
              {apiKeys.filter(k => k.revoked_at).length > 0 && (
                <div className="api-keys-section revoked-section">
                  <h4 className="section-title">
                    <span className="status-dot revoked"></span>
                    Revoked Keys
                    <span className="section-count">{apiKeys.filter(k => k.revoked_at).length}</span>
                  </h4>
                  <div className="api-keys-list">
                    {apiKeys.filter(k => k.revoked_at).map((key) => (
                      <div key={key.id} className="api-key-item revoked">
                        <div className="key-info">
                          <div className="key-header">
                            <span className="key-name">{key.name}</span>
                            <code className="key-preview">{maskApiKey(key.id)}</code>
                            <button 
                              className="btn-copy-small"
                              onClick={() => copyToClipboard(key.id, key.id)}
                              title="Copy key ID"
                            >
                              {copied === key.id ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                            {key.created_at && (
                              <span className="key-date">
                                {new Date(key.created_at).toLocaleDateString(undefined, { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            )}
                          </div>
                          <div className="key-tags">
                            {(key.permissions || []).map((perm) => (
                              <span key={perm} className="tag tag-permission">{perm}</span>
                            ))}
                            {key.ip_whitelist && key.ip_whitelist.length > 0 ? (
                              <span className="tag tag-ip" title={key.ip_whitelist.join(", ")}>
                                IPs: {key.ip_whitelist.length}
                              </span>
                            ) : (
                              <span className="tag tag-no-ip">No IP whitelist</span>
                            )}
                            <span className="tag tag-revoked">Revoked</span>
                          </div>
                        </div>
                        <div className="key-actions">
                          {/* No actions for revoked keys */}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === "docs" && (
        <div className="api-docs-content">
          <ApiDocumentation />
        </div>
      )}

      {/* Edit Modal */}
      {editingKey && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit API Key</h3>
              <button className="btn-close" onClick={closeEditModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Key Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label>Permissions</label>
                <div className="permissions-group edit-permissions">
                  {["read", "transfer", "admin"].map((perm) => (
                    <label key={perm} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={editPermissions.includes(perm)}
                        onChange={() => toggleEditPermission(perm)}
                      />
                      <span className="checkmark"></span>
                      <span className="checkbox-text">{perm}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>IP Whitelist (comma separated, leave empty for none)</label>
                <input
                  type="text"
                  value={editIpWhitelist}
                  onChange={(e) => setEditIpWhitelist(e.target.value)}
                  placeholder="e.g., 192.168.1.1, 10.0.0.1"
                  className="input-field"
                />
                <p className="form-hint">Leave empty to allow access from any IP</p>
              </div>

              <div className="modal-info">
                <p><strong>Note:</strong> The API key and secret cannot be changed or viewed after creation.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary-outline" onClick={closeEditModal}>
                Cancel
              </button>
              <button 
                className="btn-primary-outline" 
                onClick={handleUpdate}
                disabled={loading || !editName.trim() || editPermissions.length === 0}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApiKeyManager;
